import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ClipboardList } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  fetchRetailCatalog,
  fetchRetailInventory,
  scheduleRetailCycleCount,
} from "@/lib/api/retail";
import type { RetailInventoryLevel, RetailProduct } from "@/features/retail/types/datasets";
import { cn } from "@/lib/utils";

type RetailCycleCountDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

type InventoryOption = {
  level: RetailInventoryLevel;
  product: RetailProduct | null;
  available: number;
  status: "healthy" | "low" | "out";
};

const STATUS_BADGE_TONE: Record<InventoryOption["status"], string> = {
  healthy:
    "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200",
  low: "border border-amber-200/70 bg-amber-100/60 text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200",
  out: "border border-rose-200/70 bg-rose-100/60 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/15 dark:text-rose-200",
};

const deriveStatus = (product: RetailProduct | null, available: number): InventoryOption["status"] => {
  if (!Number.isFinite(available) || available <= 0) {
    return "out";
  }
  if (!product) {
    return "healthy";
  }
  if (product.reorderPoint !== null && available <= product.reorderPoint) {
    return "low";
  }
  if (product.safetyStock !== null && available <= product.safetyStock) {
    return "low";
  }
  return "healthy";
};

const EMPTY_PRODUCTS: RetailProduct[] = [];
const EMPTY_LEVELS: RetailInventoryLevel[] = [];
const MAX_CYCLE_COUNT_LEVELS = 50;

const arraysShallowEqual = (a: string[], b: string[]) => {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }
  return true;
};

const normalizeSelection = (ids: string[]): string[] => {
  if (ids.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    normalized.push(id);
    if (normalized.length === MAX_CYCLE_COUNT_LEVELS) {
      break;
    }
  }
  return normalized;
};

export const RetailCycleCountDialog = ({ open, onOpenChange, onSuccess }: RetailCycleCountDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const inventoryQuery = useQuery({
    queryKey: ["retail", "inventory"],
    queryFn: ({ signal }) => fetchRetailInventory({ signal }),
    enabled: open,
    staleTime: 15 * 1000,
  });

  const products = catalogQuery.data ?? EMPTY_PRODUCTS;
  const productById = useMemo(() => {
    const map = new Map<string, RetailProduct>();
    for (const product of products) {
      map.set(product.id, product);
    }
    return map;
  }, [products]);

  const inventoryLevels = inventoryQuery.data ?? EMPTY_LEVELS;
  const inventoryOptions = useMemo<InventoryOption[]>(() => {
    return inventoryLevels.map((level) => {
      const product = productById.get(level.productId) ?? null;
      const available = Math.max(0, level.onHand - level.allocated);
      return {
        level,
        product,
        available,
        status: deriveStatus(product, available),
      };
    });
  }, [inventoryLevels, productById]);

  const lowAndOutIds = useMemo(
    () =>
      inventoryOptions
        .filter((option) => option.status !== "healthy")
        .map((option) => option.level.id),
    [inventoryOptions],
  );

  const [selectedLevelIds, setSelectedLevelIds] = useState<string[]>([]);
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setSelectedLevelIds((previous) => (previous.length === 0 ? previous : []));
      if (note.length > 0) {
        setNote("");
      }
      return;
    }

    const nextSelection =
      lowAndOutIds.length > 0
        ? normalizeSelection(lowAndOutIds)
        : inventoryOptions.length > 0
          ? normalizeSelection([inventoryOptions[0].level.id])
          : [];

    setSelectedLevelIds((previous) => {
      if (arraysShallowEqual(previous, nextSelection)) {
        return previous;
      }
      return [...nextSelection];
    });
  }, [open, lowAndOutIds, inventoryOptions, note]);

  const cycleMutation = useMutation({
    mutationFn: scheduleRetailCycleCount,
    onSuccess: (tasks) => {
      toast({
        title: t("retail.quickActions.cycle.toastSuccess", { defaultValue: "Cycle count scheduled" }),
        description: t("retail.quickActions.cycle.toastDescription", {
          defaultValue: "{{count}} locations queued for verification.",
          count: tasks.length,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "dashboard"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("retail.quickActions.cycle.toastError", { defaultValue: "Unable to schedule cycle count" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const isLoading = catalogQuery.isLoading || inventoryQuery.isLoading;
  const hasError = catalogQuery.isError || inventoryQuery.isError;
  const disableSubmit = cycleMutation.isPending || selectedLevelIds.length === 0;

  const selectButtonLabel =
    lowAndOutIds.length > 0
      ? t("retail.quickActions.cycle.selectPriority", { defaultValue: "Select low & out" })
      : t("retail.quickActions.cycle.selectAll", { defaultValue: "Select all" });

  const toggleSelection = (levelId: string, checked: boolean) => {
    setSelectedLevelIds((previous) => {
      if (checked) {
        if (previous.includes(levelId)) {
          return previous;
        }
        const next = normalizeSelection([...previous, levelId]);
        if (next.length === previous.length) {
          toast({
            title: t("retail.quickActions.cycle.selectionLimitTitle", {
              defaultValue: "Maximum selections reached",
            }),
            description: t("retail.quickActions.cycle.selectionLimitDescription", {
              defaultValue: "Select up to 50 inventory positions per cycle count request.",
            }),
            variant: "destructive",
          });
          return previous;
        }
        return next;
      }
      return previous.filter((value) => value !== levelId);
    });
  };

  const handleSelectPriority = () => {
    if (lowAndOutIds.length === 0) {
      setSelectedLevelIds(normalizeSelection(inventoryOptions.map((option) => option.level.id)));
      return;
    }
    setSelectedLevelIds(normalizeSelection(lowAndOutIds));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedLevelIds.length === 0) {
      toast({
        title: t("retail.quickActions.cycle.missingSelectionTitle", {
          defaultValue: "Choose at least one location",
        }),
        description: t("retail.quickActions.cycle.missingSelectionDescription", {
          defaultValue: "Select the inventory positions that need verification.",
        }),
        variant: "destructive",
      });
      return;
    }

    cycleMutation.mutate({
      levelIds: selectedLevelIds,
      note: note.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-emerald-500 dark:text-emerald-300" />
            {t("retail.quickActions.cycle.title", { defaultValue: "Start cycle count" })}
          </DialogTitle>
          <DialogDescription>
            {t("retail.quickActions.cycle.description", {
              defaultValue: "Pick the shelves or variants that should be double-checked by the floor team.",
            })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-lg bg-slate-200/70 dark:bg-slate-800/60" />
            <Skeleton className="h-24 w-full rounded-lg bg-slate-200/70 dark:bg-slate-800/60" />
            <Skeleton className="h-20 w-full rounded-lg bg-slate-200/70 dark:bg-slate-800/60" />
          </div>
        ) : hasError ? (
          <div className="rounded-lg border border-rose-200/60 bg-rose-50/60 p-4 text-sm text-rose-600 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
            {t("retail.quickActions.cycle.errorState", {
              defaultValue: "We could not load inventory positions. Try again shortly.",
            })}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="flex items-center justify-between gap-2">
              <div className="space-y-1">
                <Label>
                  {t("retail.quickActions.cycle.selectionLabel", { defaultValue: "Inventory locations" })}
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("retail.quickActions.cycle.selectionSummary", {
                    defaultValue: "{{selected}} selected out of {{total}}",
                    selected: selectedLevelIds.length,
                    total: inventoryOptions.length,
                  })}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleSelectPriority}
                disabled={inventoryOptions.length === 0 || cycleMutation.isPending}
              >
                {selectButtonLabel}
              </Button>
            </div>

            {inventoryOptions.length === 0 ? (
              <div className="rounded-lg border border-slate-200/70 bg-slate-50/70 p-4 text-sm text-slate-600 dark:border-slate-800/60 dark:bg-slate-900/60 dark:text-slate-300">
                {t("retail.quickActions.cycle.emptyState", {
                  defaultValue: "All products already have confirmed counts.",
                })}
              </div>
            ) : (
              <ScrollArea className="max-h-60 rounded-lg border border-slate-200/70 bg-white/90 p-2 dark:border-slate-800/60 dark:bg-slate-950/60">
                <div className="space-y-2">
                  {inventoryOptions.map((option) => {
                    const checked = selectedLevelIds.includes(option.level.id);
                    const productName = option.product?.name ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" });
                    const locationName =
                      option.level.locationName ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" });
                    return (
                      <label
                        key={option.level.id}
                        className={cn(
                          "flex items-start gap-3 rounded-lg border border-slate-200/70 bg-white px-3 py-2 text-left transition-colors dark:border-slate-800/60 dark:bg-slate-950/50",
                          checked ? "border-emerald-300/70 dark:border-emerald-400/60" : null,
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => toggleSelection(option.level.id, value === true)}
                          className="mt-[3px]"
                          disabled={cycleMutation.isPending}
                        />
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-slate-100">{productName}</span>
                            <Badge
                              className={cn(
                                "rounded-full px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide",
                                STATUS_BADGE_TONE[option.status],
                              )}
                            >
                              {option.status === "healthy"
                                ? t("retail.inventory.table.status.healthy", { defaultValue: "Healthy" })
                                : option.status === "low"
                                ? t("retail.inventory.table.status.low", { defaultValue: "Low" })
                                : t("retail.inventory.table.status.out", { defaultValue: "Out" })}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {locationName} • {t("retail.quickActions.cycle.availableLabel", {
                              defaultValue: "On hand {{count}}",
                              count: option.available,
                            })}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            <div className="space-y-2">
              <Label htmlFor="cycle-note">
                {t("retail.quickActions.cycle.noteLabel", { defaultValue: "Notes (optional)" })}
              </Label>
              <Textarea
                id="cycle-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t("retail.quickActions.cycle.notePlaceholder", {
                  defaultValue: "Add context for the associate completing this count.",
                })}
                disabled={cycleMutation.isPending}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={cycleMutation.isPending}
              >
                {t("common.actions.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button type="submit" disabled={disableSubmit}>
                {cycleMutation.isPending
                  ? t("retail.quickActions.cycle.submitPending", { defaultValue: "Scheduling…" })
                  : t("retail.quickActions.cycle.submitLabel", { defaultValue: "Schedule count" })}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RetailCycleCountDialog;
